<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { api, messageFrom } from '../api'
import StatusBadge from '../components/StatusBadge.vue'
import UploadZone from '../components/UploadZone.vue'
import { formatBytes, formatDate, formatDuration } from '../format'
import type { Recording, Speaker, SpeakerKnowledge } from '../types'

const props = defineProps<{ id: string }>()
const emit = defineEmits<{ navigate: [path: string] }>()
const speaker = ref<Speaker | null>(null)
const recordings = ref<Recording[]>([])
const knowledge = ref<SpeakerKnowledge | null>(null)
const loading = ref(true)
const rebuilding = ref(false)
const settingVoice = ref(false)
const voiceRecordingId = ref('')
const voiceStartSec = ref(600)
const voiceDurationSec = ref(30)
const error = ref('')
let pollTimer: number | null = null

async function load(silent = false): Promise<void> {
  if (!silent) loading.value = true
  try {
    const response = await api<{ speaker: Speaker; recordings: Recording[]; knowledge: SpeakerKnowledge }>(`/api/admin/speakers/${props.id}`)
    speaker.value = response.speaker
    recordings.value = response.recordings
    knowledge.value = response.knowledge
    if (!voiceRecordingId.value && response.recordings.length) {
      voiceRecordingId.value = response.recordings[0]?.id ?? ''
    }
    error.value = ''
  } catch (reason) { error.value = messageFrom(reason) }
  finally { loading.value = false }
}

async function rename(): Promise<void> {
  if (!speaker.value) return
  const name = window.prompt('Новое имя спикера', speaker.value.name)
  if (!name || name.trim() === speaker.value.name) return
  try {
    speaker.value = (await api<{ speaker: Speaker }>(`/api/admin/speakers/${props.id}`, {
      method: 'PATCH', body: JSON.stringify({ name }),
    })).speaker
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function removeFolder(): Promise<void> {
  if (!speaker.value) return
  let confirmName = ''
  if (recordings.value.length) {
    confirmName = window.prompt(`В папке есть записи. Чтобы удалить её вместе со всеми файлами, введите точно:\n${speaker.value.name}`) ?? ''
    if (confirmName !== speaker.value.name) return
  } else if (!window.confirm(`Удалить пустую папку «${speaker.value.name}»?`)) return
  try {
    await api(`/api/admin/speakers/${props.id}`, { method: 'DELETE', body: JSON.stringify({ confirmName }) })
    emit('navigate', '/admin')
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function renameRecording(recording: Recording): Promise<void> {
  const title = window.prompt('Новое название записи', recording.title)
  if (!title || title.trim() === recording.title) return
  try {
    await api(`/api/admin/recordings/${recording.id}`, { method: 'PATCH', body: JSON.stringify({ title }) })
    await load(true)
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function removeRecording(recording: Recording): Promise<void> {
  if (!window.confirm(`Удалить запись «${recording.title}» и её аудиофайл? Отменить это действие нельзя.`)) return
  try {
    await api(`/api/admin/recordings/${recording.id}`, { method: 'DELETE', body: JSON.stringify({ confirmed: true }) })
    await load(true)
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function retranscribe(recording: Recording): Promise<void> {
  if (!window.confirm(`Снова поставить «${recording.title}» на расшифровку? Готовые части будут взяты из кэша.`)) return
  try {
    await api(`/api/admin/recordings/${recording.id}/retranscribe`, { method: 'POST', body: '{}' })
    await load(true)
  } catch (reason) { window.alert(messageFrom(reason)) }
}

async function rebuildKnowledge(): Promise<void> {
  if (!speaker.value || rebuilding.value
    || !window.confirm(`Заново определить границы, нарезать записи и собрать карточку «${speaker.value.name}»?`)) return
  rebuilding.value = true
  try {
    await api(`/api/admin/speakers/${props.id}/rebuild-knowledge`, { method: 'POST', body: '{}' })
    await load(true)
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { rebuilding.value = false }
}

async function setVoiceSample(): Promise<void> {
  if (!speaker.value || !voiceRecordingId.value || settingVoice.value) return
  settingVoice.value = true
  try {
    speaker.value = (await api<{ speaker: Speaker }>(`/api/admin/speakers/${props.id}/voice-sample`, {
      method: 'POST',
      body: JSON.stringify({
        recordingId: voiceRecordingId.value,
        startSec: voiceStartSec.value,
        durationSec: voiceDurationSec.value,
      }),
    })).speaker
  } catch (reason) { window.alert(messageFrom(reason)) }
  finally { settingVoice.value = false }
}

onMounted(async () => {
  await load()
  pollTimer = window.setInterval(() => {
    if (recordings.value.some(item => item.status === 'queued' || item.status === 'transcribing'
      || ['pending', 'detecting', 'embedding'].includes(item.knowledgeStatus))
      || knowledge.value?.profileStatus === 'pending' || knowledge.value?.profileStatus === 'generating') void load(true)
  }, 3_000)
})
onBeforeUnmount(() => { if (pollTimer !== null) window.clearInterval(pollTimer) })
</script>

<template>
  <main class="page">
    <button class="back-link" type="button" @click="emit('navigate', '/admin')">← Все папки</button>
    <p v-if="loading" class="quiet-message">Загружаем записи…</p>
    <div v-else-if="speaker">
      <header class="page-heading page-heading--actions">
        <div><p class="eyebrow">Папка спикера</p><h1>{{ speaker.name }}</h1></div>
        <div class="button-row">
          <button class="button button--quiet" type="button" @click="rename">Переименовать</button>
          <button class="button button--danger-quiet" type="button" @click="removeFolder">Удалить папку</button>
        </div>
      </header>

      <UploadZone :speaker-id="speaker.id" @uploaded="load(true)" />

      <section class="voice-card">
        <div class="section-heading">
          <div>
            <p class="eyebrow">Озвучка ответов</p>
            <h2>Голос спикера</h2>
          </div>
          <strong :class="speaker.voiceConfigured ? 'text-success' : ''">
            {{ speaker.voiceConfigured ? 'Голос задан' : 'Голос не задан' }}
          </strong>
        </div>
        <audio
          v-if="speaker.voiceSampleAvailable && speaker.voiceSampleUrl"
          controls
          preload="none"
          :src="speaker.voiceSampleUrl"
        >
          Браузер не поддерживает аудиоплеер.
        </audio>
        <p v-else-if="speaker.voiceConfigured" class="settings-hint">
          Голос зарегистрирован в DeepInfra. Локального образца для переслушивания пока нет.
        </p>
        <div v-if="recordings.length" class="voice-sample-form">
          <label for="voice-recording">Запись для образца</label>
          <select id="voice-recording" v-model="voiceRecordingId">
            <option v-for="recording in recordings" :key="recording.id" :value="recording.id">
              {{ recording.title }}
            </option>
          </select>
          <div class="voice-sample-times">
            <label>
              Начало, сек
              <input v-model.number="voiceStartSec" type="number" min="0" step="1">
            </label>
            <label>
              Длина, сек
              <input v-model.number="voiceDurationSec" type="number" min="5" max="120" step="1">
            </label>
          </div>
          <p class="settings-hint">По умолчанию берём 30 секунд с десятой минуты записи.</p>
          <button class="button button--quiet" type="button" :disabled="settingVoice" @click="setVoiceSample">
            {{ settingVoice ? 'Режем и регистрируем…' : 'Задать образец голоса' }}
          </button>
        </div>
        <p v-else class="settings-hint">Сначала добавьте хотя бы одну запись.</p>
      </section>

      <section v-if="knowledge" class="knowledge-card">
        <div class="section-heading">
          <div>
            <p class="eyebrow">База знаний</p>
            <h2>Карточка спикера</h2>
          </div>
          <button
            class="button button--quiet"
            type="button"
            :disabled="rebuilding || !knowledge.eligibleRecordings"
            @click="rebuildKnowledge"
          >
            {{ rebuilding ? 'Ставим в очередь…' : 'Пересобрать знания' }}
          </button>
        </div>
        <dl class="knowledge-stats">
          <div><dt>Обработано записей</dt><dd>{{ knowledge.processedRecordings }} из {{ knowledge.eligibleRecordings }}</dd></div>
          <div><dt>Смысловых кусков</dt><dd>{{ knowledge.chunkCount }}</dd></div>
          <div><dt>Карточка</dt><dd>{{ knowledge.profileUpdatedAt ? formatDate(knowledge.profileUpdatedAt) : 'ещё не собрана' }}</dd></div>
        </dl>
        <p v-if="knowledge.profileStatus === 'pending' || knowledge.profileStatus === 'generating'" class="knowledge-progress">
          {{ knowledge.profileStatus === 'generating' ? 'Собираем карточку…' : 'Карточка ждёт обработки записей…' }}
        </p>
        <p v-if="knowledge.profileStatus === 'error' && knowledge.profileError" class="recording-error">
          {{ knowledge.profileError }}
        </p>
        <div v-if="knowledge.profile" class="speaker-profile">{{ knowledge.profile }}</div>
      </section>

      <section class="recordings-section">
        <div class="section-heading">
          <h2>Записи</h2>
          <div v-if="recordings.some(item => item.status === 'done')" class="export-links">
            <a class="button button--quiet" :href="`/api/admin/speakers/${speaker.id}/export.txt`">Один TXT</a>
            <a class="button button--quiet" :href="`/api/admin/speakers/${speaker.id}/export.zip`">Скачать ZIP</a>
          </div>
        </div>
        <p v-if="!recordings.length" class="empty-state">В этой папке пока нет записей.</p>
        <article v-for="recording in recordings" :key="recording.id" class="recording-row">
          <button class="recording-row__main" type="button" @click="emit('navigate', `/admin/recordings/${recording.id}`)">
            <strong>{{ recording.title }}</strong>
            <span>{{ formatDate(recording.createdAt) }} · {{ formatDuration(recording.durationSec) }} · {{ formatBytes(recording.sizeBytes) }}</span>
            <StatusBadge :recording="recording" />
          </button>
          <p v-if="recording.status === 'error' && recording.error" class="recording-error">{{ recording.error }}</p>
          <p v-else-if="recording.knowledgeStatus === 'error' && recording.knowledgeError" class="recording-error">
            База знаний: {{ recording.knowledgeError }}
          </p>
          <div class="row-actions">
            <button type="button" @click="renameRecording(recording)">Переименовать</button>
            <button v-if="recording.status === 'done' || recording.status === 'error'" type="button" @click="retranscribe(recording)">Перетранскрибировать</button>
            <button class="danger-link" type="button" @click="removeRecording(recording)">Удалить</button>
          </div>
        </article>
      </section>
    </div>
    <p v-if="error" class="field-error" role="alert">{{ error }}</p>
  </main>
</template>
